/* ==========================================================================
   Theme base JS
   - Header mobile menu toggle
   - Quantity selectors
   - Product variant picker + media swap
   - Newsletter async submit hint
   - Announcement bar marquee duplication
   ========================================================================== */
(() => {
  const onReady = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  const money = (cents) => {
    try {
      const format = window.Shopify?.money_format ?? '${{amount}}';
      const value = (cents / 100).toFixed(2);
      return format.replace(/\{\{\s*amount\s*\}\}/, value);
    } catch { return `${(cents/100).toFixed(2)}`; }
  };

  /* Mobile header menu (drawer) */
  onReady(() => {
    const toggle = document.querySelector('.site-header__menu-toggle');
    const drawer = document.querySelector('[data-mobile-nav]');
    const overlay = document.querySelector('[data-nav-overlay]');
    const closeBtn = document.querySelector('[data-nav-close]');
    if (!toggle || !drawer) return;

    function openNav() {
      drawer.setAttribute('data-open', 'true');
      toggle.setAttribute('aria-expanded', 'true');
      overlay?.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }
    function closeNav() {
      drawer.setAttribute('data-open', 'false');
      toggle.setAttribute('aria-expanded', 'false');
      overlay?.classList.remove('is-active');
      document.body.style.overflow = '';
    }
    toggle.addEventListener('click', openNav);
    closeBtn?.addEventListener('click', closeNav);
    overlay?.addEventListener('click', closeNav);
    drawer.querySelectorAll('a').forEach(a => a.addEventListener('click', closeNav));
  });

  /* Language selector toggle */
  onReady(() => {
    const selector = document.querySelector('[data-lang-selector]');
    if (!selector) return;
    const btn = selector.querySelector('.lang-selector__toggle');
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = selector.dataset.open === 'true';
      selector.dataset.open = open ? 'false' : 'true';
      btn.setAttribute('aria-expanded', !open);
    });
    document.addEventListener('click', () => {
      selector.dataset.open = 'false';
      btn.setAttribute('aria-expanded', 'false');
    });
  });

  /* Marquee: duplicate content so animation loops smoothly */
  onReady(() => {
    document.querySelectorAll('.announcement__track').forEach((track) => {
      const originalHTML = track.innerHTML;
      track.innerHTML = originalHTML + originalHTML;
    });
  });

  /* Quantity selectors */
  onReady(() => {
    document.querySelectorAll('.quantity').forEach((el) => {
      const input = el.querySelector('input[type="number"]');
      if (!input) return;
      el.querySelectorAll('[data-qty-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = Number(btn.dataset.qtyStep) || 1;
          const min = Number(input.min) || 0;
          const next = Math.max(min, Number(input.value || 0) + step);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  });

  /* Product media gallery — thumb click swaps main */
  onReady(() => {
    document.querySelectorAll('[data-media-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-media-main]');
      const thumbs = gallery.querySelectorAll('[data-media-thumb]');
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const src = thumb.dataset.mediaSrc;
          const alt = thumb.dataset.mediaAlt || '';
          if (!src || !main) return;
          const img = main.querySelector('img');
          if (img) { img.srcset = ''; img.src = src; img.alt = alt; }
          thumbs.forEach((t) => t.setAttribute('aria-current', 'false'));
          thumb.setAttribute('aria-current', 'true');
        });
      });
    });
  });

  /* Variant picker — resolves current variant, updates URL, price, media */
  onReady(() => {
    document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
      const productJsonEl = document.querySelector(`[data-product-json="${picker.dataset.productHandle}"]`);
      if (!productJsonEl) return;
      let product;
      try { product = JSON.parse(productJsonEl.textContent); } catch { return; }

      const form = picker.closest('form');
      const priceEl = document.querySelector('[data-product-price]');
      const gallery = document.querySelector('[data-media-gallery]');

      const getSelected = () => {
        const values = [];
        picker.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
          values[Number(input.dataset.optionIndex)] = input.value;
        });
        return values;
      };

      const findVariant = (selected) => {
        return product.variants.find((v) =>
          v.options.every((o, i) => o === selected[i])
        );
      };

      const update = () => {
        const selected = getSelected();
        const variant = findVariant(selected);
        if (!variant) return;

        // URL
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        // Hidden id input
        if (form) {
          const idInput = form.querySelector('input[name="id"]');
          if (idInput) idInput.value = variant.id;
        }

        // Price
        if (priceEl) {
          const price = money(variant.price);
          const compare = variant.compare_at_price && variant.compare_at_price > variant.price
            ? `<s class="price__compare">${money(variant.compare_at_price)}</s>` : '';
          priceEl.innerHTML = `<span class="price__sale">${price}</span> ${compare}`;
        }

        // Availability
        const btn = document.querySelector('[data-add-to-cart]');
        if (btn) {
          btn.disabled = !variant.available;
          btn.textContent = variant.available
            ? btn.dataset.labelAdd
            : btn.dataset.labelSoldOut;
        }

        // Featured media
        if (gallery && variant.featured_image) {
          const main = gallery.querySelector('[data-media-main] img');
          if (main) { main.src = variant.featured_image; }
        }
      };

      picker.addEventListener('change', update);
    });
  });

  /* Add to cart AJAX (drawer only) */
  onReady(() => {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        if (document.body.dataset.cartType !== 'drawer') return;
        event.preventDefault();
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;

        try {
          const formData = new FormData(form);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });
          if (!res.ok) throw new Error('add_failed');
          window.dispatchEvent(new CustomEvent('cart:updated'));
          window.dispatchEvent(new CustomEvent('cart:open'));
        } catch (err) {
          form.submit();
        } finally {
          if (submitBtn) submitBtn.disabled = false;
        }
      });
    });
  });
})();
